import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, GuardResult, Router, RouterStateSnapshot } from '@angular/router';
import { HeaderService } from 'onego-header';
import { firstValueFrom } from 'rxjs';
import { skip } from 'rxjs/operators';

import { TenantService } from '../../mtp-common/services';
import { AppStateService } from '../../mtp-common/services/state';

const TENANT_SWITCH_USER_RELOAD_TIMEOUT_MS = 10000;

@Injectable({
    providedIn: 'root',
})
export class AuthGuardService implements CanActivate {
    private readonly headerService = inject(HeaderService);
    private readonly appState = inject(AppStateService);
    private readonly tenantService = inject(TenantService);
    private readonly router = inject(Router);

    public async canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Promise<GuardResult> {
        if (!this.headerService.isAuthenticated()) {
            console.debug('User is not authenticated, redirecting to login flow');
            this.headerService.login(_state.url);
            return false;
        }

        // Check if a tenant is specified in the route parameters, use it if possible.
        const tenantId = _route.queryParamMap.get('tenant');
        const user = this.appState.user.value;
        let hasSwitchedTenant = false;
        if (tenantId && user) {
            const tenant = user.tenants.find((tenant) => tenant.id === tenantId);
            if (tenant && tenantId !== this.appState.activeTenantId.value) {
                console.warn(`Switching to newly provided tenant (${tenantId})`);
                hasSwitchedTenant = true;

                // Set up a promise to wait for the user to be reloaded later
                const userReloaded = firstValueFrom(this.appState.user.stream.pipe(skip(1)));

                // Switch tenant
                this.tenantService.selectTenant(user, tenant);

                // Wait for the user to be reloaded after selecting the new tenant, to ensure permissions are updated.
                try {
                    await Promise.race([
                        userReloaded,
                        new Promise((_, reject) =>
                            setTimeout(
                                () =>
                                    reject(
                                        new Error(
                                            'Expected user to be reloaded after switching tenant, but the operation timed out.'
                                        )
                                    ),
                                TENANT_SWITCH_USER_RELOAD_TIMEOUT_MS
                            )
                        ),
                    ]);
                } catch (e) {
                    console.warn(e);
                }
                return true;
            }
        }

        // User is authenticated. Check if a tenant is selected.
        if (this.appState.loggedInWithTenant.value) {
            return true;
        }

        if (!hasSwitchedTenant) {
            console.debug('No tenant selected, redirecting to tenant selection', _state);
            this.appState.returnUrl.value = _state.url;
        }

        return this.router.createUrlTree(['tenant']);
    }
}
